import {
  parseCreditApplicationPayload,
  parseTemporaryCreditApplicationPayload,
  type ApprovalRequestRecord,
} from '@nadoutong/contracts';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useMyApprovals } from '../../api/approvals.ts';
import { DualCurrencyAmount } from '../../components/DualCurrencyAmount.tsx';
import { CustomerCell } from '../../components/identity/CustomerCell.tsx';
import { CustomerRef } from '../../components/identity/CustomerRef.tsx';
import { OpsUserRef } from '../../components/identity/OpsUserRef.tsx';
import { formatLocalTimestamp } from '../../components/formatters.ts';
import { coerceErrorMessage } from '../../components/Toast.tsx';
import { useT } from '../../i18n/index.ts';
import { deriveApprovalTimeline } from '../../lib/approvals.ts';
import {
  Button,
  Chip,
  EmptyState,
  Pagination,
  Table,
  TableEmpty,
  TableSkeleton,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../../ui/index.ts';
import { approvalOrgTargetId, KIND_LABEL_KEYS, STATUS_LABEL_KEYS, statusChipTone } from './kindPresentation.ts';

/**
 * Requester "my submissions" tab + the expandable status timeline
 * (extracted from ApprovalCenterPage.tsx in E3-G2 #1103, #1098
 * deferred item 18 — behavior-preserving move).
 */

const PAGE_SIZE = 50;

export function MineTab(): JSX.Element {
  const t = useT();
  const [offset, setOffset] = useState(0);
  const query = useMyApprovals({ limit: PAGE_SIZE, offset }, true);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  function toggle(id: string): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  if (query.isPending) {
    return <TableSkeleton rows={5} testId="approvals-mine-loading" />;
  }
  const data = query.data;
  if (query.isError || data === undefined) {
    return (
      <EmptyState
        title={t('approvals.load_error')}
        hint={coerceErrorMessage(query.error)}
        action={
          <Button
            onClick={() => {
              void query.refetch();
            }}
          >
            {t('common.retry')}
          </Button>
        }
        testId="approvals-mine-error"
      />
    );
  }

  const pageCount = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <div className="flex flex-col gap-3">
      <Table testId="approvals-mine-table">
        <THead>
          <Tr interactive={false}>
            <Th>{t('approvals.col.kind')}</Th>
            <Th>{t('approvals.col.customer')}</Th>
            <Th>{t('approvals.col.amount')}</Th>
            <Th>{t('approvals.col.cycle')}</Th>
            <Th>{t('approvals.col.target')}</Th>
            <Th>{t('approvals.col.status')}</Th>
            <Th>{t('approvals.col.submitted_at')}</Th>
            <Th>{t('approvals.col.timeline')}</Th>
          </Tr>
        </THead>
        <TBody>
          {data.requests.length === 0 ? (
            <TableEmpty colSpan={8} testId="approvals-mine-empty">
              {t('approvals.mine.empty')}
            </TableEmpty>
          ) : (
            data.requests.map((request) => {
              const isExpanded = expanded.has(request.id);
              const creditPayload =
                request.kind === 'credit_application'
                  ? parseCreditApplicationPayload(request.payload)
                  : request.kind === 'temporary_credit_application'
                    ? parseTemporaryCreditApplicationPayload(request.payload)
                    : null;
              const cycleDays =
                creditPayload !== null && 'requested_cycle_days' in creditPayload
                  ? creditPayload.requested_cycle_days
                  : null;
              return [
                <Tr key={request.id} data-testid={`approval-mine-row-${request.id}`}>
                  <Td emphasis>{t(KIND_LABEL_KEYS[request.kind])}</Td>
                  <Td>
                    <CustomerCell
                      orgId={approvalOrgTargetId(request)}
                      secondary={
                        request.credit_customer?.org_name ?? approvalOrgTargetId(request) ?? undefined
                      }
                      testId={`approval-mine-customer-${request.id}`}
                    />
                  </Td>
                  <Td num>
                    {creditPayload === null ? (
                      t('credit.overview.dash')
                    ) : (
                      <DualCurrencyAmount
                        usdMicros={null}
                        cnyCents={creditPayload.requested_limit_cny_cents}
                        signed={false}
                      />
                    )}
                  </Td>
                  <Td num>
                    {cycleDays === null
                      ? t('credit.overview.dash')
                      : t('credit.kpi.days', { days: String(cycleDays) })}
                  </Td>
                  <Td mono>
                    <MineApprovalTargetRef request={request} />
                  </Td>
                  <Td>
                    <Chip tone={statusChipTone(request.status)}>
                      {t(STATUS_LABEL_KEYS[request.status])}
                    </Chip>
                  </Td>
                  <Td mono>{formatLocalTimestamp(request.created_at)}</Td>
                  <Td>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => toggle(request.id)}
                      aria-expanded={isExpanded}
                      data-testid={`approval-timeline-toggle-${request.id}`}
                    >
                      {isExpanded ? (
                        <ChevronDown aria-hidden="true" className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronRight aria-hidden="true" className="h-3.5 w-3.5" />
                      )}
                      {isExpanded ? t('approvals.mine.collapse') : t('approvals.mine.expand')}
                    </Button>
                  </Td>
                </Tr>,
                isExpanded ? (
                  <Tr key={`${request.id}-timeline`} interactive={false}>
                    <Td colSpan={8} className="bg-well/50">
                      <RequestTimeline request={request} />
                    </Td>
                  </Tr>
                ) : null,
              ];
            })
          )}
        </TBody>
      </Table>
      {pageCount > 1 ? (
        <Pagination
          page={Math.floor(offset / PAGE_SIZE) + 1}
          pageCount={pageCount}
          onPageChange={(page) => setOffset((page - 1) * PAGE_SIZE)}
          prevLabel={t('common.previous')}
          nextLabel={t('common.next')}
          ariaLabel={t('approvals.pagination.aria_label')}
        />
      ) : null}
    </div>
  );
}

function RequestTimeline({ request }: { readonly request: ApprovalRequestRecord }): JSX.Element {
  const t = useT();
  const steps = deriveApprovalTimeline(request);
  return (
    <ol
      className="m-0 flex list-none flex-col gap-2 p-0"
      data-testid={`approval-timeline-${request.id}`}
    >
      {steps.map((step) => (
        <li
          key={`${step.toStatus}-${step.stage ?? 'terminal'}-${step.at}`}
          className="flex flex-col gap-0.5"
        >
          <div className="flex flex-wrap items-center gap-2">
            <Chip tone={statusChipTone(step.toStatus)}>
              {step.stage !== undefined
                ? t('approvals.timeline.stage_cleared', { stage: String(step.stage) })
                : step.toStatus === 'pending'
                  ? t('approvals.timeline.submitted')
                  : t(STATUS_LABEL_KEYS[step.toStatus])}
            </Chip>
            <span className="font-mono text-label text-paper-mid">
              <OpsUserRef id={step.actorOpsUserId} />
            </span>
            {/*
              #2265: what admitted this actor to the stage. Rendered only
              when recorded — stage decisions predating #2265 carry nothing,
              and showing "by stage role" for them would assert something
              the row does not actually say.
            */}
            {step.qualifiedBy !== undefined ? (
              <Chip
                tone={step.qualifiedBy === 'admin_bypass' ? 'warn' : 'neutral'}
                lamp={false}
                testId="approval-timeline-qualified-by"
              >
                {step.qualifiedBy === 'admin_bypass'
                  ? t('approvals.timeline.qualified_admin_bypass')
                  : t('approvals.timeline.qualified_stage_role')}
              </Chip>
            ) : null}
            <span className="font-mono text-label text-paper-low">
              {formatLocalTimestamp(step.at)}
            </span>
          </div>
          {step.note !== null && step.note.length > 0 ? (
            <p className="m-0 pl-1 text-dense text-paper-mid" data-testid="approval-timeline-note">
              {step.note}
            </p>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

function MineApprovalTargetRef({ request }: { readonly request: ApprovalRequestRecord }): JSX.Element {
  const orgId = approvalOrgTargetId(request);
  if (orgId === null) {
    return (
      <>
        {request.target_type}:{request.target_id}
      </>
    );
  }
  return (
    <>
      {request.target_type}:<CustomerRef orgId={orgId} />
    </>
  );
}
